import { useState } from 'react';
import { ChevronRight, Check, Clock, Shield, Users, BarChart3, Package, Activity } from 'lucide-react';

export default function SAPAMSComponent() {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'services', label: 'Services' },
    { id: 'benefits', label: 'Benefits' }
  ];

  return (
    <div className="w-full bg-gray-50 p-6 font-sans">
      <div className=" mx-auto">
        {/* Header */}
        <div style={{ backgroundColor: '#0A2647' }} className=" p-8 mb-8 text-white">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2 text-white">SAP AMS Expertise</h1>
              <p className="text-xl font-light text-white">Delivered with Care</p>
            </div>
            <div className="mt-4 md:mt-0 px-4 py-2 rounded-full" style={{ backgroundColor: '#FF8C32' }}>
              <p className="text-sm font-medium">Small enough to care, big enough to deliver</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 font-medium text-sm ${
                activeTab === tab.id 
                  ? 'border-b-2 text-blue-800 border-blue-500' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <p className="text-gray-700 leading-relaxed">
                Maintaining efficient supply chain operations is integral to any business, and requires a strong 
                SAP AMS partner to reduce TCO and ensure systems continue to operate at peak performance levels. 
                With experienced SAP technical and functional experts accessible on-demand, at any time, our SAP AMS 
                covers a comprehensive range of modules on both S/4HANA and ECC.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg p-6 shadow-sm border-l-4" style={{ borderLeftColor: '#3498DB' }}>
                <h3 className="text-xl font-semibold mb-3">Responsive Support</h3>
                <p className="text-gray-600">24/7 global coverage with expert consultants available when you need them most.</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm border-l-4" style={{ borderLeftColor: '#2ECC71' }}>
                <h3 className="text-xl font-semibold mb-3">High-Quality Projects</h3>
                <p className="text-gray-600">Relish in peace of mind with our steadfast commitment to compliance and excellence.</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm border-l-4" style={{ borderLeftColor: '#9B59B6' }}>
                <h3 className="text-xl font-semibold mb-3">Simplified Administration</h3>
                <p className="text-gray-600">Managing your sales, invoicing, contracts, project management, and payments with ease.</p>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#F0F9FF' }}>
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold">Thriving Community</h3>
              </div>
              <p className="text-gray-600">
                We foster an active community where talented individuals can collaborate on challenging projects and 
                network with peers, providing a supportive environment for growth and innovation.
              </p>
            </div>
          </div>
        )}

        {/* Services Tab Content */}
        {activeTab === 'services' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-semibold">SAP Functional AMS</h3>
                <div className="px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: '#E3F2FD', color: '#1565C0' }}>
                  S/4HANA & ECC
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                Expert support and managed services covering a comprehensive range of modules on both S/4HANA and ECC, 
                ensuring smooth and consistent end-to-end processes.
              </p>
              <button className="flex items-center text-blue-600 font-medium">
                Find Out More <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-semibold">SAP Technical AMS</h3>
                <div className="px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }}>
                  Basis & Security
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                Support and maintenance for all your SAP technical and infrastructure related needs, 
                including Basis and Security & Authorisations.
              </p>
              <button className="flex items-center text-blue-600 font-medium">
                Find Out More <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-semibold">Service Delivery Management</h3>
                <div className="px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: '#F3E5F5', color: '#7B1FA2' }}>
                  ITIL Aligned
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                Ensuring the highest quality of service delivery and satisfaction. Governance for service, 
                incident and change management and continual service improvement.
              </p>
              <button className="flex items-center text-blue-600 font-medium">
                Learn More <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-sm" style={{ backgroundColor: '#FBF9FF' }}>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#F0F0F0' }}>
                  <Package className="w-5 h-5 text-gray-700" />
                </div>
                <h3 className="text-lg font-medium">Redefining Gig Culture</h3>
              </div>
              <p className="text-gray-600">
                We offer an alternative to the traditional gig economy by providing engaging and 
                meaningful projects with fair compensation and growth opportunities.
              </p>
              <div className="mt-4 flex justify-between items-center">
                <div className="flex items-center">
                  <div className="text-xl font-bold text-green-600">$95</div>
                  <div className="text-sm text-gray-500 ml-1">per hour</div>
                </div>
                <div className="flex items-center text-gray-500">
                  <div className="text-xl font-bold text-gray-500 line-through">$20</div>
                  <div className="text-sm ml-1">traditional rate</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Benefits Tab Content */}
        {activeTab === 'benefits' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">SAP AMS that Works for You</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#EBF8FF' }}>
                  <Users className="w-6 h-6 text-blue-500" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Subject Matter Experts</h3>
                <p className="text-gray-600">
                  Our SAP AMS consultants have deep experience and expertise across the full range of functional and technical modules.
                </p>
              </div>
              
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#EBF8FF' }}>
                  <Clock className="w-6 h-6 text-blue-500" />
                </div>
                <h3 className="text-lg font-semibold mb-2">24/7 Global Coverage</h3>
                <p className="text-gray-600">
                  We operate 24/7, globally, so if something goes wrong, you can be confident that our expert consultants will be there when you need them.
                </p>
              </div>
              
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#EBF8FF' }}>
                  <Shield className="w-6 h-6 text-blue-500" />
                </div>
                <h3 className="text-lg font-semibold mb-2">ITIL Aligned Service</h3>
                <p className="text-gray-600">
                  We are ITIL aligned, giving you the confidence that our approach to SAP AMS is to a high, industry-recognised standard.
                </p>
              </div>
              
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#EBF8FF' }}>
                  <Check className="w-6 h-6 text-blue-500" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Long-term Solutions</h3>
                <p className="text-gray-600">
                  Issue resolution is often just a short-term fix. We provide problem management, including root cause analysis, to deliver long-term solutions.
                </p>
              </div>
              
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#EBF8FF' }}>
                  <Package className="w-6 h-6 text-blue-500" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Flexible Packages</h3>
                <p className="text-gray-600">
                  Our flexible approach to SAP AMS allows you to build a custom package of support that meets your individual business and operational requirements.
                </p>
              </div>
              
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#EBF8FF' }}>
                  <BarChart3 className="w-6 h-6 text-blue-500" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Real-time Monitoring & Reporting</h3>
                <p className="text-gray-600">
                  A real-time online dashboard can ensure you see the reports and data that is relevant to you, generated by KPIs tailored to your needs.
                </p>
              </div>
            </div>
            
            <div className="mt-8 p-6 rounded-lg" style={{ backgroundColor: '#F0F9FF' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Activity className="w-6 h-6 text-blue-600 mr-3" />
                  <h3 className="text-xl font-semibold">Ready to transform your SAP support?</h3>
                </div>
                <button 
                  className="px-6 py-2 rounded-md text-white font-medium" 
                  style={{ backgroundColor: '#0A2647' }}
                >
                  Contact Us
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}